// src/pages/organization/event/GetAllEvent.js

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMainPageTitle } from "../../../contexts/PageContext";
import ProfileImage from "../../../components/profileImage";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

const GetAllEvent = () => {
  const { t } = useTranslation();
  const { updatePageTitle } = useMainPageTitle();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter and Sort States
  const [filters, setFilters] = useState({
    eventType: "",
    eventStatus: "",
    search: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);

  useEffect(() => {
    updatePageTitle(t("events.title"), t("events.allEvents"));
  }, [updatePageTitle, t]);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        page: currentPage,
        eventType: filters.eventType,
        eventStatus: filters.eventStatus,
        search: filters.search,
      });

      const response = await fetch(
        `${API_URL}/event/get-all-events-by-organization-id/?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(t("errors.failedToLoad"));
      }

      const data = await response.json();
      setEvents(data.events);
      setTotalPages(data.totalPages);
      setTotalEvents(data.total);
      setError(null);
    } catch (err) {
      setError(t("errors.failedToLoad"));
      console.error("Error fetching events:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.eventType, filters.eventStatus, filters.search, t]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600">{t("common.loading")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={fetchEvents}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          {t("common.tryAgain")}
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t("events.allEvents")}</h1>
          <p className="text-gray-600 mt-2">
            {t("events.totalEvents", { count: totalEvents })}
          </p>
        </div>
        <button
          onClick={() => navigate("/organization/events/create")}
          className="bg-[#F15213] text-white px-6 py-3 rounded-lg hover:bg-[#d14711] transition-colors duration-200 flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          {t("events.createEvent")}
        </button>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("events.eventType")}
            </label>
            <select
              name="eventType"
              value={filters.eventType}
              onChange={handleFilterChange}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t("common.all")}</option>
              <option value="Training">{t("events.training")}</option>
              <option value="Match">{t("events.match")}</option>
              <option value="Meeting">{t("events.meeting")}</option>
              <option value="Other">{t("common.other")}</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("events.status")}
            </label>
            <select
              name="eventStatus"
              value={filters.eventStatus}
              onChange={handleFilterChange}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">{t("common.all")}</option>
              <option value="upcoming">{t("events.upcoming")}</option>
              <option value="inprogress">{t("events.inProgress")}</option>
              <option value="completed">{t("events.completed")}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("common.search")}
            </label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder={t("events.searchPlaceholder")}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Events Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {events.map((event) => (
          <div
            key={event._id}
            className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
            onClick={() => navigate(`/organization/events/${event._id}`)}
          >
            {/* Card Header with Gradient Background */}
            <div className="p-6 bg-gradient-to-r from-blue-500 to-purple-600">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold text-white line-clamp-2">{event.name}</h3>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ml-2 ${
                    event.eventStatus === "upcoming"
                      ? "bg-white/20 text-white"
                      : event.eventStatus === "inprogress"
                      ? "bg-green-500/20 text-white"
                      : "bg-gray-500/20 text-white"
                  }`}
                >
                  {t(`events.${event.eventStatus}`)}
                </span>
              </div>
            </div>

            <div className="p-6">
              {/* Team Information - Special handling for Match type */}
              {event.eventType === "Match" ? (
                <div className="mb-6">
                  {/* Team 1 */}
                  <div className="flex items-center mb-4 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                    <ProfileImage
                      image={event.teamLogo1 || "/default-team-logo.png"}
                      alt={event.teamName1}
                      className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-white shadow-md"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{event.teamName1}</h4>
                      <div className="flex space-x-2 text-sm text-gray-600 mt-1">
                        <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamType1}</span>
                        <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamGender1}</span>
                      </div>
                    </div>
                  </div>

                  {/* VS Indicator */}
                  <div className="flex justify-center items-center my-4">
                    <div className="px-6 py-2 bg-gradient-to-r from-red-500 to-orange-500 text-white rounded-full font-bold text-lg shadow-md">
                      {t("events.vs")}
                    </div>
                  </div>

                  {/* Team 2 */}
                  <div className="flex items-center bg-gray-50 p-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                    <ProfileImage
                      image={event.teamLogo2 || "/default-team-logo.png"}
                      alt={event.teamName2}
                      className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-white shadow-md"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900 text-lg">{event.teamName2}</h4>
                      <div className="flex space-x-2 text-sm text-gray-600 mt-1">
                        <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamType2}</span>
                        <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamGender2}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center mb-6 bg-gray-50 p-4 rounded-xl border border-gray-100 hover:bg-gray-100 transition-colors duration-200">
                  <ProfileImage
                    image={event.teamLogo || "/default-team-logo.png"}
                    alt={event.teamName}
                    className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-white shadow-md"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{event.teamName}</h4>
                    <div className="flex space-x-2 text-sm text-gray-600 mt-1">
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamType}</span>
                      <span className="px-2 py-1 bg-gray-200 rounded-full capitalize">{event.teamGender}</span>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Event Details */}
              <div className="space-y-4">
                <p className="text-gray-600 line-clamp-2 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  {event.description}
                </p>
                
                <div className="flex items-center text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-100">
                  <svg className="h-5 w-5 mr-2 text-blue-500" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                    <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="font-medium">{formatDate(event.startDate)} - {formatTime(event.startDate)}</span>
                </div>

                <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${
                    event.eventType === "Match" 
                      ? "bg-red-100 text-red-700"
                      : "bg-blue-100 text-blue-700"
                  }`}>
                    {t(`events.${event.eventType.toLowerCase()}`)}
                  </span>
                  <div className="flex items-center text-gray-600">
                    <svg className="h-5 w-5 mr-1" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                      <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <span className="font-medium">{event.participants || 0} {t("events.participants")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {events.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t("events.noEventsFound")}</h3>
          <p className="text-gray-500 mb-4">{t("events.noEventsMessage")}</p>
          <button
            onClick={() => navigate("/organization/events/create")}
            className="bg-[#F15213] text-white px-6 py-2 rounded-lg hover:bg-[#d14711] transition-colors"
          >
            {t("events.createFirstEvent")}
          </button>
        </div>
      )}

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-center mt-8 space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.previous")}
          </button>
          
          {[...Array(totalPages)].map((_, i) => (
            <button
              key={i}
              onClick={() => handlePageChange(i + 1)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                currentPage === i + 1
                  ? "bg-[#F15213] text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              {i + 1}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common.next")}
          </button>
        </div>
      )}
    </div>
  );
};

export default GetAllEvent;
